import type { DanteMotionPreference, DanteStateId, DanteView } from "./dante-character.js";
import { danteState } from "./dante-character.js";

export type DanteMaterial =
  | "ivory"
  | "russet"
  | "saddle"
  | "eyes"
  | "ink"
  | "muzzle"
  | "tab"
  | "harness"
  | "harnessHighlight"
  | "leather"
  | "brass"
  | "map";
export type DantePart = "body" | "head" | "eyes" | "ear" | "tail" | "equipment" | "paw";
export interface DanteLayer {
  id: string;
  part: DantePart;
  material: DanteMaterial;
  path: string;
  stroke?: DanteMaterial;
  strokeWidth?: number;
}

/** Surface-bound vector masks. Anatomical right is the folded ear on the viewer's left. */
export const DANTE_RIG_LAYERS: readonly DanteLayer[] = [
  {
    id: "tail",
    part: "tail",
    material: "russet",
    path: "M350 288 Q412 281 425 229 Q437 234 430 258 Q421 303 365 316 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "far-rear-paw",
    part: "body",
    material: "muzzle",
    path: "M328 323 L352 325 L355 423 Q374 427 367 440 L334 440 L322 378 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "far-front-paw",
    part: "body",
    material: "muzzle",
    path: "M194 322 L218 323 L217 423 Q237 430 229 440 L196 440 L185 374 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "coat",
    part: "body",
    material: "ivory",
    path: "M169 252 Q194 235 223 251 Q273 240 334 260 L343 254 L347 267 Q377 282 374 318 L380 326 L371 333 Q363 363 324 372 Q267 389 211 357 L200 368 L193 351 L182 355 Q155 315 169 252 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "saddle",
    part: "body",
    material: "saddle",
    path: "M220 254 Q273 240 333 262 L343 258 L348 271 Q376 291 367 323 L347 322 L328 335 L301 327 L279 333 L250 319 L225 322 L217 289 Z",
  },
  {
    id: "haunch",
    part: "body",
    material: "russet",
    path: "M321 310 Q355 316 349 359 L337 408 L341 425 Q359 426 358 440 L318 440 L311 399 L307 362 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "rear-sock",
    part: "body",
    material: "ivory",
    path: "M316 405 L339 406 L341 425 Q359 426 358 440 L318 440 Z",
  },
  {
    id: "near-front-paw",
    part: "paw",
    material: "ivory",
    path: "M216 328 L249 335 L240 387 L235 425 Q255 428 251 440 L209 440 L211 390 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "harness",
    part: "equipment",
    material: "harness",
    path: "M186 252 L213 247 Q240 274 249 322 L307 315 L312 333 L246 347 Q216 328 203 296 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "harness-binding",
    part: "equipment",
    material: "harnessHighlight",
    path: "M194 255 L203 252 Q233 286 238 324 L236 332 Q221 309 218 291 Z",
  },
  {
    id: "repaired-seam",
    part: "equipment",
    material: "leather",
    path: "M217 288 L233 283 L241 304 L224 311 Z",
    stroke: "muzzle",
    strokeWidth: 2,
  },
  {
    id: "stitches",
    part: "equipment",
    material: "muzzle",
    path: "M220 291 L230 288 L231 291 L221 294 Z M223 299 L234 295 L235 298 L224 302 Z",
  },
  {
    id: "field-pouch",
    part: "equipment",
    material: "leather",
    path: "M268 296 L311 289 L319 331 Q291 346 274 333 Z",
    stroke: "ink",
    strokeWidth: 3,
  },
  {
    id: "pouch-flap",
    part: "equipment",
    material: "leather",
    path: "M268 296 L311 289 L312 307 L290 318 L272 311 Z",
    stroke: "ink",
    strokeWidth: 3,
  },
  {
    id: "map-roll",
    part: "equipment",
    material: "ivory",
    path: "M272 264 Q269 258 277 254 L331 247 Q343 253 340 270 L282 279 Q274 276 272 264 Z",
    stroke: "ink",
    strokeWidth: 3,
  },
  {
    id: "map-lines",
    part: "equipment",
    material: "map",
    path: "M282 259 L323 254 L324 257 L283 263 Z M285 267 L312 263 L312 266 L285 270 Z",
  },
  {
    id: "map-strap",
    part: "equipment",
    material: "leather",
    path: "M300 251 L309 250 L314 275 L305 276 Z",
    stroke: "ink",
    strokeWidth: 2,
  },
  {
    id: "brass-fittings",
    part: "equipment",
    material: "brass",
    path: "M237 318 L249 316 L252 329 L240 332 Z M289 307 L295 306 L296 312 L290 313 Z",
    stroke: "ink",
    strokeWidth: 2,
  },
  {
    id: "rust-tab",
    part: "equipment",
    material: "tab",
    path: "M249 334 L259 336 L257 357 L247 354 Z",
    stroke: "ink",
    strokeWidth: 2,
  },
  {
    id: "ruff",
    part: "head",
    material: "ivory",
    path: "M174 225 L295 230 L301 256 L288 251 L289 273 L272 266 L267 285 L252 275 L244 291 L227 276 L212 285 L207 268 L190 276 L187 258 L171 261 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "upright-left-ear",
    part: "ear",
    material: "russet",
    path: "M265 168 Q263 131 288 83 Q312 108 309 170 L290 190 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "left-ear-inner",
    part: "ear",
    material: "muzzle",
    path: "M278 158 L288 107 Q301 129 297 161 L288 174 Z",
  },
  {
    id: "face",
    part: "head",
    material: "russet",
    path: "M172 163 Q194 135 238 146 Q278 140 301 177 L307 207 L319 221 L309 226 L313 244 L299 242 Q280 272 238 274 Q190 274 169 243 L156 241 L161 225 L151 221 L164 206 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "folded-right-ear",
    part: "head",
    material: "russet",
    path: "M181 161 Q161 132 141 148 Q129 166 152 204 L164 196 L173 173 L189 179 Z",
    stroke: "ink",
    strokeWidth: 4,
  },
  {
    id: "right-ear-fold",
    part: "head",
    material: "saddle",
    path: "M145 155 Q159 151 174 171 L163 189 Q155 169 145 155 Z",
  },
  {
    id: "ivory-blaze",
    part: "head",
    material: "ivory",
    path: "M226 149 L240 148 L237 176 L246 201 L265 225 L245 252 L214 244 L208 221 L222 198 L228 174 Z",
  },
  {
    id: "eye-brows",
    part: "head",
    material: "muzzle",
    path: "M184 189 L194 181 L213 185 L218 192 L200 190 Z M254 184 L274 180 L286 188 L277 190 L261 188 Z",
  },
  {
    id: "brown-eyes",
    part: "eyes",
    material: "eyes",
    path: "M190 203 Q200 191 212 203 Q202 216 190 203 Z M258 201 Q270 190 281 202 Q270 214 258 201 Z",
    stroke: "ink",
    strokeWidth: 3,
  },
  {
    id: "restrained-catchlights",
    part: "eyes",
    material: "ivory",
    path: "M200 198 L203 198 L203 201 L200 201 Z M268 197 L271 197 L271 200 L268 200 Z",
  },
  {
    id: "wiry-muzzle",
    part: "head",
    material: "ivory",
    path: "M213 216 Q236 206 257 216 L276 228 L291 230 L283 240 L295 244 L282 253 L286 263 L268 261 L266 273 L250 266 L238 275 L225 265 L209 272 L208 259 L191 263 L194 250 L182 245 L197 236 L188 229 Z",
    stroke: "ink",
    strokeWidth: 3,
  },
  {
    id: "muzzle-shadow",
    part: "head",
    material: "muzzle",
    path: "M214 247 Q237 261 264 245 L258 258 L239 263 L221 258 Z",
  },
  {
    id: "nose",
    part: "head",
    material: "ink",
    path: "M219 222 Q238 214 253 223 L249 233 L237 239 L224 234 Z",
  },
  {
    id: "mouth",
    part: "head",
    material: "ink",
    path: "M235 236 L239 236 L239 246 L251 248 L250 251 L237 249 L223 252 L222 249 L235 246 Z",
  },
  {
    id: "fixed-fur-lines",
    part: "head",
    material: "muzzle",
    path: "M199 240 L212 244 L211 246 L198 242 Z M202 252 L214 251 L214 253 L202 254 Z M264 239 L280 235 L280 237 L265 242 Z M263 251 L277 249 L277 251 L263 254 Z",
  },
] as const;

export interface DantePose {
  head: number;
  gazeX: number;
  gazeY: number;
  ear: number;
  tail: number;
  bodyX: number;
  bodyY: number;
  paw: number;
  sit: number;
  rest: number;
  breath: number;
  blink: number;
  equipment: number;
}

export interface DanteRenderParameters {
  /** Normalized relative gaze, from -1 to 1 on each axis. */
  gazeTarget?: readonly [number, number];
  /** Ground anchor within the stage, default (0.50, 0.86). */
  stagePosition?: readonly [number, number];
  expressiveAmplitude?: number;
}

export function applyDanteRenderParameters(
  pose: DantePose,
  parameters: DanteRenderParameters,
): DantePose {
  const finite = (value: number | undefined, fallback: number) =>
    value !== undefined && Number.isFinite(value) ? value : fallback;
  const amplitude = Math.max(0.5, Math.min(1.5, finite(parameters.expressiveAmplitude, 1)));
  const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));
  return {
    ...pose,
    head: clamp(pose.head * amplitude, 12),
    ear: pose.ear * amplitude,
    tail: clamp(pose.tail * amplitude, 18),
    breath: pose.breath * amplitude,
    gazeX: parameters.gazeTarget ? clamp(finite(parameters.gazeTarget[0], 0), 1) * 5 : pose.gazeX,
    gazeY: parameters.gazeTarget ? clamp(finite(parameters.gazeTarget[1], 0), 1) * 5 : pose.gazeY,
  };
}

export function danteStageTransform(position?: readonly [number, number]): string {
  const x = position && Number.isFinite(position[0]) ? position[0] : 0.5;
  const y = position && Number.isFinite(position[1]) ? position[1] : 0.86;
  return `translate(${(x - 0.5) * 512} ${(y - 0.86) * 512})`;
}

const neutral: DantePose = {
  head: 0,
  gazeX: 0,
  gazeY: 0,
  ear: 0,
  tail: 0,
  bodyX: 0,
  bodyY: 0,
  paw: 0,
  sit: 0,
  rest: 0,
  breath: 0,
  blink: 1,
  equipment: 0,
};

/** Representative poses remain distinct when motion is disabled. Degrees and canvas units. */
export const DANTE_POSES: Record<DanteStateId, Partial<DantePose>> = {
  "quiet-watch": { head: -2 },
  "recognize-companion": { head: -5, ear: -6, tail: 12 },
  "listen-completely": { head: 6, ear: -8, gazeX: -2 },
  "shared-attention": { head: -5, gazeX: 4, gazeY: 2 },
  "survey-situation": { head: -6, gazeX: -4 },
  "notice-discrepancy": { head: -3, gazeY: -3, ear: -10 },
  "recall-relevant": { head: -7, gazeX: 4, gazeY: 5 },
  "prepare-next": { head: -4, gazeX: 5, bodyX: 4, ear: -5 },
  "read-evidence": { head: 5, gazeY: 5, gazeX: -3 },
  "compare-possibilities": { head: -4, gazeX: 5, gazeY: 3 },
  "check-hypothesis": { head: 4, gazeY: 5, paw: 14 },
  "bring-findings-together": { head: 2, gazeY: 4 },
  "leave-question-open": { head: 10, gazeX: 2, ear: -4 },
  "ask-useful-question": { head: 7, sit: 1, gazeY: -2 },
  "revise-conclusion": { head: -6, gazeX: -4, bodyX: -3 },
  "supported-answer": { head: 1, gazeX: 0, ear: -2 },
  "keep-counsel": { head: -7, gazeX: 4, gazeY: 6, bodyX: -2 },
  "offer-route": { head: -5, gazeX: 5, paw: 7 },
  "wait-decision": { head: 0, bodyX: -8, gazeY: -1 },
  "begin-deliberately": { head: -3, bodyX: 7, paw: 5, gazeX: 4 },
  "steady-progress": { head: 3, gazeY: 4, paw: 3 },
  "respect-obstacle": { head: 6, gazeY: 4, bodyX: -5, ear: -6 },
  "better-route": { head: -8, gazeX: -5, bodyX: -7, paw: 6 },
  "pause-cleanly": { head: 7, gazeX: -4, bodyX: -2 },
  "acknowledge-completion": { head: 3, tail: 12, gazeX: -1 },
  "accept-praise": { head: 5, tail: 14, ear: -4 },
  "give-credit": { head: -6, gazeX: -5, bodyX: 10 },
  "own-mistake": { head: 6, gazeY: 2 },
  "put-it-right": { head: 5, gazeY: 6, gazeX: 2, paw: 2 },
  "state-limit": { head: 0, sit: 1, bodyX: -8 },
  "make-room": { head: -5, gazeX: -5, bodyX: 13 },
  "rest-attentively": { rest: 1, head: 4, ear: -5 },
};

const ease = (t: number) => {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
};

/** Transition poses remain separate from semantic states. A rise takes longer than a glance. */
export function blendDantePose(from: DantePose, to: DantePose, elapsedMs: number): DantePose {
  const duration = Math.abs(from.rest - to.rest) > 0.1 ? 1100 : 220;
  const blended = { ...to };
  for (const key of Object.keys(to) as (keyof DantePose)[]) {
    const delay = key === "head" ? 125 : key === "equipment" ? 250 : 0;
    const amount = ease((elapsedMs - delay) / duration);
    blended[key] = from[key] + (to[key] - from[key]) * amount;
  }
  return blended;
}

/** Samples a clip without producing events, progress values, authorization, or results. */
export function sampleDantePose(
  id: DanteStateId,
  elapsedMs: number,
  motion: DanteMotionPreference = "full",
): DantePose {
  const target = { ...neutral, ...DANTE_POSES[id] };
  if (motion !== "full") return target;
  const state = danteState(id);
  const t = Math.max(0, elapsedMs);
  const entryMs = state.playback === "loop" ? 220 : state.durationMs;
  const entry = ease(t / entryMs);
  const headEntry = ease((t - 125) / entryMs);
  const pose = { ...target };
  for (const key of Object.keys(pose) as (keyof DantePose)[]) {
    pose[key] = neutral[key] + (target[key] - neutral[key]) * (key === "head" ? headEntry : entry);
  }
  const phase = (t % state.durationMs) / state.durationMs;
  const wave = Math.sin(phase * 2 * Math.PI);
  const active = state.playback === "loop" || t < state.durationMs;
  if (active && (id === "survey-situation" || id === "compare-possibilities")) {
    pose.gazeX = 4 * wave;
    pose.head = 6 * Math.sin((Math.max(0, t - 125) / state.durationMs) * 2 * Math.PI);
  }
  if (active && id === "read-evidence") pose.gazeX = wave * 3;
  if (
    active &&
    (id === "recognize-companion" || id === "acknowledge-completion" || id === "accept-praise")
  ) {
    pose.tail =
      Math.sin(
        Math.min(1, t / state.durationMs) * Math.PI * (id === "recognize-companion" ? 4 : 2),
      ) * target.tail;
  } else pose.tail = 0;
  if (active && (id === "steady-progress" || id === "put-it-right"))
    pose.gazeX += wave * (id === "put-it-right" ? 0.6 : 1.2);
  // Breathing changes the torso only; contact anchors remain fixed.
  pose.breath = Math.sin((t / 5000) * Math.PI * 2) * 1.5;
  const blink = t % 7000;
  pose.blink = blink > 6600 ? 1 - Math.sin(((blink - 6600) / 400) * Math.PI) * 0.9 : 1;
  pose.equipment =
    Math.sin((Math.max(0, t - 250) / 700) * Math.PI) * Math.max(0, 1 - t / 1000) * 1.5;
  return pose;
}

/** Shared SVG transforms keep the same rig and contact geometry on web and native. */
export function dantePartTransform(
  part: DantePart,
  pose: DantePose,
  view: DanteView = "front-left",
): string {
  // Project the bilateral torso from the other side. The asymmetric head and ears
  // have their own placement and are never reflected with the torso.
  const torsoView = view === "front-right" ? "translate(512 0) scale(-1 1)" : "";
  const body = `translate(${pose.bodyX} ${pose.bodyY})`;
  const torso = `${body} ${torsoView}`;
  const head = `translate(${view === "front-right" ? 70 : 0} ${pose.sit * 20 + pose.rest * 130}) rotate(${pose.head} 235 250)`;
  switch (part) {
    case "head":
      return `${body} ${head}`;
    case "eyes":
      return `${body} ${head} translate(${pose.gazeX} ${pose.gazeY}) translate(0 203) scale(1 ${pose.blink}) translate(0 -203)`;
    case "ear":
      return `${body} ${head} rotate(${pose.ear} 286 164)`;
    case "tail":
      return `${torso} translate(0 ${pose.rest * 65}) rotate(${pose.tail} 360 300)`;
    case "equipment":
      return `${torso} translate(0 ${pose.breath + pose.sit * 20 + pose.rest * 75 + pose.equipment})`;
    case "paw":
      return `translate(${pose.bodyX} ${-pose.paw}) ${torsoView} translate(225 440) scale(1 ${1 - pose.rest * 0.65}) translate(-225 -440)`;
    case "body":
      return `${torso} translate(0 440) scale(1 ${1 - pose.sit * 0.13 - pose.rest * 0.5 + pose.breath / 188}) translate(0 -440)`;
  }
}
